import $ from '../lib/jquery';
import './owl-carousel-2.1.6';

/**
 * Autoplay Plugin
 * @version 2.1.0
 * @author Bartosz Wojciechowski
 * @author Artus Kolanowski
 * @author David Deutsch
 * @license The MIT License (MIT)
 */

/**
 * Creates the autoplay plugin.
 * @class The Autoplay Plugin
 * @param {Owl} scope - The Owl Carousel
 */
export const Autoplay = function (carousel) {
  /**
   * Reference to the core.
   * @protected
   * @type {Owl}
   */
  this._core = carousel;

  /**
   * The autoplay timeout.
   * @type {Timeout}
   */
  this._timeout = null;

  /**
   * Indicates whenever the autoplay is paused.
   * @type {Boolean}
   */
  this._paused = false;

  /**
   * All event handlers.
   * @protected
   * @type {Object}
   */
  this._handlers = {
    'changed.owl.carousel': $.proxy(function (e) {
      if (e.namespace && e.property.name === 'settings') {
        if (this._core.settings.autoplay) {
          this.play();
        } else {
          this.stop();
        }
      } else if (e.namespace && e.property.name === 'position') {
        //console.log('play?', e);
        if (this._core.settings.autoplay) {
          this._setAutoPlayInterval();
        }
      }
    }, this),
    'initialized.owl.carousel': $.proxy(function (e) {
      if (e.namespace && this._core.settings.autoplay) {
        this.play();
      }
    }, this),
    'play.owl.autoplay': $.proxy(function (e, t, s) {
      if (e.namespace) {
        this.play(t, s);
      }
    }, this),
    'stop.owl.autoplay': $.proxy(function (e) {
      if (e.namespace) {
        this.stop();
      }
    }, this),
    'mouseover.owl.autoplay': $.proxy(function () {
      if (this._core.settings.autoplayHoverPause && this._core.is('rotating')) {
        this.pause();
      }
    }, this),
    'mouseleave.owl.autoplay': $.proxy(function () {
      if (this._core.settings.autoplayHoverPause && this._core.is('rotating')) {
        this.play();
      }
    }, this),
    'touchstart.owl.core': $.proxy(function () {
      if (this._core.settings.autoplayHoverPause && this._core.is('rotating')) {
        this.pause();
      }
    }, this),
    'touchend.owl.core': $.proxy(function () {
      if (this._core.settings.autoplayHoverPause) {
        this.play();
      }
    }, this)
  };

  // register event handlers
  this._core.$element.on(this._handlers);

  // set default options
  this._core.options = $.extend({}, Autoplay.Defaults, this._core.options);
};

/**
 * Default options.
 * @public
 */
Autoplay.Defaults = {
  autoplay: false,
  autoplayTimeout: 5000,
  autoplayHoverPause: false,
  autoplaySpeed: false
};

/**
 * Starts the autoplay.
 * @public
 * @param {Number} [timeout] - The interval before the next animation starts.
 * @param {Number} [speed] - The animation speed for the animations.
 */
Autoplay.prototype.play = function (timeout, speed) {
  this._paused = false;

  if (this._core.is('rotating')) {
    return;
  }

  this._core.enter('rotating');

  this._setAutoPlayInterval();
};

/**
 * Gets a new timeout
 * @private
 * @param {Number} [timeout] - The interval before the next animation starts.
 * @param {Number} [speed] - The animation speed for the animations.
 * @return {Timeout}
 */
Autoplay.prototype._getNextTimeout = function (timeout, speed) {
  if (this._timeout) {
    window.clearTimeout(this._timeout);
  }
  return window.setTimeout($.proxy(function () {
    if (this._paused || this._core.is('busy') || this._core.is('interacting') || document.hidden) {
      return;
    }
    this._core.next(speed || this._core.settings.autoplaySpeed);
  }, this), timeout || this._core.settings.autoplayTimeout);
};

/**
 * Sets autoplay in motion.
 * @private
 */
Autoplay.prototype._setAutoPlayInterval = function () {
  this._timeout = this._getNextTimeout();
};

/**
 * Stops the autoplay.
 * @public
 */
Autoplay.prototype.stop = function () {
  if (!this._core.is('rotating')) {
    return;
  }

  window.clearTimeout(this._timeout);
  this._core.leave('rotating');
};

/**
 * Stops the autoplay.
 * @public
 */
Autoplay.prototype.pause = function () {
  if (!this._core.is('rotating')) {
    return;
  }

  this._paused = true;
};

/**
 * Destroys the plugin.
 */
Autoplay.prototype.destroy = function () {
  var handler,
    property;

  this.stop();

  for (handler in this._handlers) {
    this._core.$element.off(handler, this._handlers[handler]);
  }
  for (property in Object.getOwnPropertyNames(this)) {
    typeof this[property] != 'function' && (this[property] = null);
  }
};

$.fn.owlCarousel.Constructor.Plugins.autoplay = Autoplay;

